import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useFlowColors } from '../useFlowColors';
import { ProcessFlowMapEdge, ProcessPathCard } from './model';
import StatusBadge from './StatusBadge';
import MetricRow, { MetricRowIcon } from './MetricRow';
import {
    FLOW_CARD_BORDER_Y,
    FLOW_CARD_PADDING_Y,
    FLOW_DOT_DIAMETER,
    FLOW_DOT_HIT_SIZE,
    ACTUAL_VS_EXPECTED_LEGEND,
    FLOW_MAP_LEGEND_BANDS,
    FLOW_MAP_LEGEND_ROWS,
    FLOW_NODE_WIDTH,
    FLOW_LAYOUT_PADDING,
    FlowDirection,
    FlowSelection,
    RECEIVED_ROW_KEY,
    receivedValue,
    computeFlowLayout,
    flowCardHeight,
    describeSelection,
    edgesForSelection,
    emphasisedNodeKeys,
    flowDirectionPhrase,
    flowDotAnchor,
    flowDotLabel,
    formatFlowNumber,
    hasEdgesInDirection,
    mapCardMetrics,
    metricRowLabel,
    nextFlowSelection,
    processPathLabel,
} from './utils';

export interface VolumeFlowMapViewProps {
    /** The flow nodes. Same per-path cards the Andon view renders. */
    nodes?: ProcessPathCard[];
    /** Directed flow edges between those nodes. */
    edges?: ProcessFlowMapEdge[];
}

/** Opacity applied to a card that is neither selected nor connected to the selection. */
const DIMMED_OPACITY = 0.3;

// Colours come from useFlowColors (Meridian theme tokens), so the map follows dark mode: a live
// connector and the active dot take the primary colour, idle ones the subdued ink, and a dot
// with nothing to reveal the disabled border colour.

/*
 * Canvas stacking order. The canvas has no z-index of its own, so the layers would
 * otherwise paint in DOM order: the node cards come last and have an opaque background,
 * so they would cut every connector that crosses them.
 *
 * Node cards are deliberately left at `z-index: auto` as the base layer. Giving them a
 * z-index would make each card a stacking context and trap its own dots below the
 * connectors.
 */
/** Connectors, above the cards so a line crossing a card stays unbroken. */
const CONNECTOR_LAYER_Z = 1;
/** Edge labels and dots, above the connectors so a stroke never crosses either. */
const OVERLAY_LAYER_Z = 2;

/** The legend strip above the map: the health colour bands and the metric icons. */
const FlowMapLegend: React.FC = () => {
    const colors = useFlowColors();
    return (
    <Row spacing="400" alignmentVertical="center" wrap="down" data-testid="flow-map-legend">
        <Text type="b100" color="secondary">Legend</Text>
        {FLOW_MAP_LEGEND_BANDS.map((band) => (
            <Row
                key={band.status}
                spacing="100"
                alignmentVertical="center"
                widths="fit"
                wrap="down"
                data-testid={`flow-map-legend-band-${band.status}`}
            >
                {/* Decorative swatch — the band's name is the text right next to it. */}
                <span
                    aria-hidden="true"
                    style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: colors.statusAccent(band.status),
                    }}
                />
                <Text type="b100" color="secondary">{band.label}</Text>
            </Row>
        ))}
        {FLOW_MAP_LEGEND_ROWS.map((rowKey) => (
            <Row
                key={rowKey}
                spacing="100"
                alignmentVertical="center"
                widths="fit"
                wrap="down"
                data-testid={`flow-map-legend-row-${rowKey}`}
            >
                <MetricRowIcon rowKey={rowKey} />
                <Text type="b100" color="secondary">{metricRowLabel(rowKey)}</Text>
            </Row>
        ))}
        <Text type="b100" color="secondary" data-testid="flow-map-legend-reading">
            <strong>{ACTUAL_VS_EXPECTED_LEGEND.sample}</strong> = {ACTUAL_VS_EXPECTED_LEGEND.meaning}
        </Text>
    </Row>
    );
};

/**
 * One directional dot handle on a card's edge — the only control that reveals flow.
 *
 * A real `<button>`, so it is tabbable, activates on Enter *and* Space and reports whether
 * it is the active selection through `aria-pressed`, with no hand-rolled key handling. Its
 * `aria-label` names the node and the direction ("Show flows into Eurosort"), so the two
 * dots on one card are never two identically-named controls.
 *
 * The button box is `FLOW_DOT_HIT_SIZE` (24px) square while the drawn circle is only
 * `FLOW_DOT_DIAMETER` (10px) across: the visible dot is deliberately discreet, but the
 * thing you have to hit is not. The circle is the button's only child and is `aria-hidden`
 * — the label carries the meaning.
 *
 * `disabled` is the real attribute, not a style: a node with no edges in this direction
 * (the first inbound node has nothing flowing in) must not be a control that looks live
 * and does nothing. A disabled button is also, correctly, skipped by Tab.
 *
 * Position comes from `flowDotAnchor` — the exact point a connector for this direction
 * terminates at — converted to the card's local coordinates, so the dot and the line meet.
 */
const FlowMapDotHandle: React.FC<{
    nodeKey: string;
    nodeLabel: string;
    direction: FlowDirection;
    /** The card's own left/top/height from the layout; the anchor is derived from them. */
    cardX: number;
    cardY: number;
    cardHeight: number;
    isActive: boolean;
    isDisabled: boolean;
    onActivate: (nodeKey: string, direction: FlowDirection) => void;
}> = ({
    nodeKey,
    nodeLabel,
    direction,
    cardX,
    cardY,
    cardHeight,
    isActive,
    isDisabled,
    onActivate,
}) => {
    const colors = useFlowColors();
    const anchor = flowDotAnchor({ x: cardX, y: cardY, height: cardHeight }, direction);
    // The dot is a child of the absolutely-positioned card, so shift the canvas anchor into
    // the card's frame and centre the hit box on it.
    const left = anchor.x - cardX - FLOW_DOT_HIT_SIZE / 2;
    const top = anchor.y - cardY - FLOW_DOT_HIT_SIZE / 2;
    const diameter = isActive ? FLOW_DOT_DIAMETER + 2 : FLOW_DOT_DIAMETER;

    return (
        <button
            type="button"
            data-testid={`flow-map-dot-${nodeKey}-${direction}`}
            data-active={isActive ? 'true' : 'false'}
            aria-label={flowDotLabel(nodeLabel, direction)}
            aria-pressed={isActive}
            disabled={isDisabled}
            onClick={(event) => {
                // The canvas clears the selection on a background click; a dot click is not
                // a background click.
                event.stopPropagation();
                onActivate(nodeKey, direction);
            }}
            style={{
                position: 'absolute',
                left,
                top,
                width: FLOW_DOT_HIT_SIZE,
                height: FLOW_DOT_HIT_SIZE,
                // A ~24px target around a 10px dot, so the control is reachable by pointer
                // and touch without inflating the drawing.
                margin: 0,
                padding: 0,
                border: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 0,
                cursor: isDisabled ? 'default' : 'pointer',
                // A connector terminates at the target's left-edge centre, which is exactly
                // where the inbound dot sits, so the raised connector layer would otherwise
                // paint its arrowhead over this handle.
                zIndex: OVERLAY_LAYER_Z,
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    display: 'block',
                    width: diameter,
                    height: diameter,
                    borderRadius: '50%',
                    background: isDisabled ? colors.disabled : isActive ? colors.primary : colors.idle,
                    // A white rim keeps the dot readable where it sits over the card's
                    // coloured accent border.
                    border: `1px solid ${colors.surface}`,
                    // A second, non-colour signal for the active dot: it grows and gains a
                    // ring, so "which dot is on" does not rest on colour alone.
                    boxShadow: isActive ? `0 0 0 2px ${colors.primary}` : 'none',
                }}
            />
        </button>
    );
};

/**
 * One node card: a plain, non-interactive container holding the two dot handles.
 *
 * The card body is deliberately NOT a control. Selection is directional and the card
 * already has two controls on it, so making the body a button as well would both lose the
 * direction and nest a `<button>` inside a `<button>` — invalid, and unusable with a
 * screen reader.
 * The body has no role, no tabindex and no activation; it only stops a stray click on itself
 * from reaching the canvas's clear handler.
 *
 * Health drives a left border and a background tint via the shared dashboard colour
 * helpers (`getStatusAccentColor` / `getStatusBannerBackgroundColor`) — this view does not
 * map status to colour itself. The status badge carries the same information as text plus
 * a glyph, so colour is never the only signal.
 *
 * `received` renders as a single number: the contract gives it no target and no variance,
 * so there is nothing to compare it against.
 *
 * `height` comes from the layout, which sized this card from its own rows. It is applied as
 * a `minHeight` and nothing is clipped: the card owns enough room for its content, and if a
 * row ever turns out taller than the arithmetic predicted (a long label wrapping, say) the
 * card grows instead of slicing the row in half.
 */
const FlowMapNodeCard: React.FC<{
    card: ProcessPathCard;
    nodeKey: string;
    x: number;
    y: number;
    height: number;
    /** True when this node is the selection, in either direction. */
    isSelected: boolean;
    /** The selected direction when this node is the selection, else null. */
    activeDirection: FlowDirection | null;
    isDimmed: boolean;
    /** Whether anything flows in / out on this map, which enables each dot. */
    hasInbound: boolean;
    hasOutbound: boolean;
    onActivateDot: (nodeKey: string, direction: FlowDirection) => void;
}> = ({
    card,
    nodeKey,
    x,
    y,
    height,
    isSelected,
    activeDirection,
    isDimmed,
    hasInbound,
    hasOutbound,
    onActivateDot,
}) => {
    const colors = useFlowColors();
    const accent = colors.statusAccent(card.nodeHealthStatus);
    const label = processPathLabel(card.topologyNode?.processPath);
    return (
        <div
            data-testid={`flow-map-node-${nodeKey}`}
            data-node-key={nodeKey}
            data-dimmed={isDimmed ? 'true' : 'false'}
            data-selected={isSelected ? 'true' : 'false'}
            data-direction={activeDirection ?? ''}
            // Not a control: no role, no tabindex, nothing to activate. This only keeps a
            // click on the card body from bubbling to the canvas and clearing the selection.
            onClick={(event) => event.stopPropagation()}
            style={{
                position: 'absolute',
                left: x,
                top: y,
                width: FLOW_NODE_WIDTH,
                minHeight: height,
                boxSizing: 'border-box',
                textAlign: 'left',
                background: colors.statusBackground(card.nodeHealthStatus),
                // Per-side rather than `border` + `borderLeft`: the selection changes the
                // hairline while the accent stripe stays, and mixing a shorthand with a
                // longhand for the same property is how that kind of update goes wrong.
                borderTop: `1px solid ${isSelected ? accent : colors.hairline}`,
                borderRight: `1px solid ${isSelected ? accent : colors.hairline}`,
                borderBottom: `1px solid ${isSelected ? accent : colors.hairline}`,
                borderLeft: `4px solid ${accent}`,
                borderRadius: 4,
                // A second, non-colour signal for the selected card, so selection does not
                // rest on the outline colour alone.
                boxShadow: isSelected ? `0 0 0 2px ${accent}` : 'none',
                padding: '8px 10px',
                // No clipping: the card is sized from its own rows, and the dots hang over
                // its left and right edges, so hiding the overflow would cut both.
                overflow: 'visible',
                opacity: isDimmed ? DIMMED_OPACITY : 1,
            }}
        >
            <Column spacing="100">
                <Text type="b300">{label}</Text>
                <StatusBadge status={card.nodeHealthStatus} />
                {receivedValue(card) !== undefined && (
                    <MetricRow
                        rowKey={RECEIVED_ROW_KEY}
                        actualValue={receivedValue(card) as number}
                        data-testid={`flow-map-node-row-${nodeKey}-${RECEIVED_ROW_KEY}`}
                    />
                )}
                {mapCardMetrics(card).map((metric, index) => (
                    <MetricRow
                        key={`${metric.metricName}-${index}`}
                        rowKey={metric.metricName}
                        actualValue={metric.actualValue}
                        expectedValue={metric.expectedValue}
                        variancePercentage={metric.variancePercentage}
                        data-testid={`flow-map-node-row-${nodeKey}-${metric.metricName}`}
                    />
                ))}
            </Column>

            {/* Both dots render on every card, for a consistent silhouette; the one with
                nothing to show in its direction is disabled rather than absent. */}
            <FlowMapDotHandle
                nodeKey={nodeKey}
                nodeLabel={label}
                direction="in"
                cardX={x}
                cardY={y}
                cardHeight={height}
                isActive={activeDirection === 'in'}
                isDisabled={!hasInbound}
                onActivate={onActivateDot}
            />
            <FlowMapDotHandle
                nodeKey={nodeKey}
                nodeLabel={label}
                direction="out"
                cardX={x}
                cardY={y}
                cardHeight={height}
                isActive={activeDirection === 'out'}
                isDisabled={!hasOutbound}
                onActivate={onActivateDot}
            />
        </div>
    );
};

/**
 * The Volume Flow map view: the same process paths as the Andon cards, laid out
 * left-to-right by process stage (Inbound → First pass → Second pass → Outbound).
 *
 * Connectors are shown on demand, not always, and one direction at a time. With nothing
 * selected the map is plain cards — a full topology of arrows is unreadable at station
 * scale. Each card carries two dot handles on its left and right edges: the left one draws
 * only what flows *into* that node and emphasises its upstream neighbours, the right one
 * only what flows *out of* it and emphasises its downstream neighbours. Every other card
 * dims, which the reference calls overlay mode.
 *
 * The selection is therefore a node *and* a direction. Pressing the active dot again,
 * clicking the background, or pressing Escape returns to the plain state; pressing the
 * other dot on the same card switches direction instead of clearing.
 *
 * Dimming is never the only signal of what is connected: the selection summary below the
 * canvas names the connected paths for the selected direction in text, each drawn edge
 * carries an `aria-label` naming both of its endpoints, and the active dot reports
 * `aria-pressed`.
 *
 * Layout is computed from the data by `computeFlowLayout` — no graph library (the repo has
 * none and we are not adding one). The one DOM read is each card's rendered height: a card
 * whose metric row wraps is taller than the arithmetic predicts, so it is measured after
 * render and the layout re-stacked, keeping the gap to the card below. Connectors are plain SVG paths on
 * a layer behind the absolutely-positioned HTML cards, and the dots are anchored by
 * `flowDotAnchor` on the same coordinates the connectors terminate at, so the three can
 * never drift apart.
 */
const VolumeFlowMapView: React.FC<VolumeFlowMapViewProps> = ({ nodes, edges }) => {
    // Rendered card heights, kept only for the nodes they were measured on: new data starts
    // from the arithmetic again.
    const [measured, setMeasured] = useState<{
        nodes: ProcessPathCard[] | undefined;
        heights: Record<string, number>;
    }>({ nodes, heights: {} });
    const measuredHeights = measured.nodes === nodes ? measured.heights : undefined;
    const layout = computeFlowLayout(nodes, edges, measuredHeights);
    const colors = useFlowColors();
    const [selection, setSelection] = useState<FlowSelection | null>(null);
    const canvasRef = useRef<HTMLDivElement>(null);

    // After every render, each card's height follows its content: a wrapped row grows it, and a
    // row that stops wrapping (a zoom, a late font) shrinks it back, never below the arithmetic.
    // The content is measured rather than the card box, whose minHeight is the recorded height,
    // so the measurement does not feed on itself and this settles in one extra render.
    const measureCards = () => {
        const canvas = canvasRef.current;
        if (canvas == null) return;
        const byKey = new Map(layout.nodes.map((node) => [node.key, node]));
        const changed: Record<string, number> = {};
        canvas.querySelectorAll<HTMLElement>('[data-node-key]').forEach((element) => {
            const node = byKey.get(element.dataset.nodeKey as string);
            const content = element.firstElementChild as HTMLElement | null;
            if (node == null || content == null) return;
            const natural = content.offsetHeight + FLOW_CARD_PADDING_Y * 2 + FLOW_CARD_BORDER_Y * 2;
            // What the layout would give this card with the new measurement.
            const target = Math.max(flowCardHeight(node.card), natural);
            if (Math.abs(target - node.height) > 0.5) changed[node.key] = natural;
        });
        if (Object.keys(changed).length === 0) return;
        setMeasured((previous) => ({
            nodes,
            heights: { ...(previous.nodes === nodes ? previous.heights : {}), ...changed },
        }));
    };
    // The resize observer outlives renders, so it calls the latest measure through a ref.
    const measureCardsRef = useRef(measureCards);

    useLayoutEffect(() => {
        measureCardsRef.current = measureCards;
        measureCards();
    });

    // A late web font or a zoom can reflow a card after the first measurement. Re-subscribed
    // only when the set of cards changes, not on every render (a selection change, say).
    const nodeKeys = layout.nodes.map((node) => node.key).join('|');
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas == null || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => measureCardsRef.current());
        canvas.querySelectorAll('[data-node-key]').forEach((element) => observer.observe(element));
        return () => observer.disconnect();
    }, [nodeKeys]);

    const clearSelection = useCallback(() => setSelection(null), []);
    const activateDot = useCallback(
        (nodeKey: string, direction: FlowDirection) =>
            setSelection((current) => nextFlowSelection(current, nodeKey, direction)),
        []
    );

    // Escape clears the selection from anywhere on the page, which is also the keyboard
    // equivalent of the background click below. Bound on the document rather than a
    // container so it works whichever dot currently holds focus.
    useEffect(() => {
        if (selection == null) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') clearSelection();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [selection, clearSelection]);

    if (layout.nodes.length === 0) {
        return (
            <Text type="b300" color="secondary" data-testid="flow-map-graph-empty">
                No flow nodes for this interval.
            </Text>
        );
    }

    const visibleEdges = edgesForSelection(layout.edges, selection);
    const emphasised = emphasisedNodeKeys(visibleEdges, selection);
    const summary = describeSelection(layout, selection);

    return (
        <Column spacing="300" data-testid="flow-map-graph-view">
            <FlowMapLegend />

            <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
                {/* Clicking empty canvas clears the selection. Escape (above) is the
                    keyboard equivalent, so this is a convenience, not the only route. */}
                <div
                    ref={canvasRef}
                    data-testid="flow-map-canvas"
                    onClick={clearSelection}
                    style={{
                        position: 'relative',
                        width: layout.width,
                        height: layout.height,
                        minWidth: layout.width,
                    }}
                >
                    {/* Decorative connector layer — every drawn edge's numbers and both of
                        its endpoints are also in the HTML labels below. */}
                    <svg
                        width={layout.width}
                        height={layout.height}
                        aria-hidden="true"
                        focusable="false"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: CONNECTOR_LAYER_Z,
                            pointerEvents: 'none',
                        }}
                    >
                        <defs>
                            <marker
                                id="flow-map-arrow"
                                markerWidth={8}
                                markerHeight={8}
                                refX={7}
                                refY={4}
                                orient="auto"
                            >
                                <path d="M 0 0 L 8 4 L 0 8 z" fill={colors.idle} />
                            </marker>
                        </defs>
                        {visibleEdges.map((layoutEdge) => (
                            <path
                                key={layoutEdge.key}
                                data-testid={`flow-map-connector-${layoutEdge.key}`}
                                d={layoutEdge.path}
                                fill="none"
                                stroke={layoutEdge.edge.isLive ? colors.primary : colors.idle}
                                strokeWidth={2}
                                strokeDasharray={layoutEdge.edge.isLive ? undefined : '5 4'}
                                markerEnd="url(#flow-map-arrow)"
                            />
                        ))}
                    </svg>

                    {/* One heading per stage, not per slot: a stage holding an edge between
                        two of its own cards spans several card slots, and `column.width`
                        covers all of them so the heading sits over the whole stage. */}
                    {layout.columns.map((column) => (
                        <div
                            key={column.processType}
                            data-testid={`flow-map-column-${column.processType}`}
                            style={{
                                position: 'absolute',
                                left: column.x,
                                top: FLOW_LAYOUT_PADDING,
                                width: column.width,
                            }}
                        >
                            <Text type="h100" color="secondary">
                                {column.label.toUpperCase()} ({column.count})
                            </Text>
                        </div>
                    ))}

                    {visibleEdges.map((layoutEdge) => (
                        <div
                            key={`label-${layoutEdge.key}`}
                            data-testid={`flow-map-edge-label-${layoutEdge.key}`}
                            aria-label={
                                `${layoutEdge.fromLabel} to ${layoutEdge.toLabel}: ` +
                                `${formatFlowNumber(layoutEdge.edge.flowUnits)} units, ` +
                                `${formatFlowNumber(layoutEdge.edge.flowSplitPercentage)}% of outflow`
                            }
                            style={{
                                position: 'absolute',
                                left: layoutEdge.labelX,
                                top: layoutEdge.labelY,
                                transform: 'translate(-50%, -50%)',
                                background: colors.surface,
                                border: `1px solid ${colors.hairline}`,
                                borderRadius: 10,
                                padding: '1px 6px',
                                whiteSpace: 'nowrap',
                                zIndex: OVERLAY_LAYER_Z,
                            }}
                        >
                            {/* The visible label is the split alone, for a clean map; the units stay in the aria-label. */}
                            <Text type="b100" color="secondary">
                                {formatFlowNumber(layoutEdge.edge.flowSplitPercentage)}%
                            </Text>
                        </div>
                    ))}

                    {layout.nodes.map((layoutNode) => (
                        <FlowMapNodeCard
                            key={layoutNode.key}
                            card={layoutNode.card}
                            nodeKey={layoutNode.key}
                            x={layoutNode.x}
                            y={layoutNode.y}
                            height={layoutNode.height}
                            isSelected={selection != null && selection.key === layoutNode.key}
                            // The direction only applies to the selected card, so every
                            // other card's dots are both inactive.
                            activeDirection={
                                selection != null && selection.key === layoutNode.key
                                    ? selection.direction
                                    : null
                            }
                            isDimmed={
                                selection != null && emphasised.indexOf(layoutNode.key) === -1
                            }
                            hasInbound={hasEdgesInDirection(layout.edges, layoutNode.key, 'in')}
                            hasOutbound={hasEdgesInDirection(layout.edges, layoutNode.key, 'out')}
                            onActivateDot={activateDot}
                        />
                    ))}
                </div>
            </div>

            {layout.edges.length === 0 ? (
                <Text type="b200" color="secondary" data-testid="flow-map-no-edges">
                    No flow connections reported for this interval.
                </Text>
            ) : summary == null ? (
                <Text type="b200" color="secondary" data-testid="flow-map-selection-hint">
                    Select a process path’s left dot to show what flows in, or its right dot
                    to show what flows out.
                </Text>
            ) : (
                <Text
                    type="b200"
                    color="secondary"
                    role="status"
                    data-testid="flow-map-selection-summary"
                >
                    {/* The empty case is a guard, not a state the UI can reach: a dot with
                        no edges in its direction is disabled, so it cannot be selected. */}
                    {`${summary.label} selected · ${flowDirectionPhrase(summary.direction)}: ` +
                        `${summary.connected.length === 0 ? 'nothing on this map' : summary.connected.join(', ')}` +
                        `. Press Escape to clear.`}
                </Text>
            )}
        </Column>
    );
};

export default VolumeFlowMapView;
